import { Redirect, Route } from "wouter";
import { MsalProvider, MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType, PublicClientApplication } from "@azure/msal-browser";
import { ChakraProvider } from '@chakra-ui/react';
import DocumentLibrary from "./pages/document_library";
import NotificationPane from "./components/NotificationPane";
import ValidUserRoute from "./util/ValidUserRoute";
import "ag-grid-enterprise";
import { LicenseManager } from "ag-grid-enterprise";
import "./styles/customScrollbar.css";
import "./styles/ag-grid-edb_custom.css";
import DynamicMenu from "./components/Sidebar";
import { theme } from "./util/theme";
import { CurrentUserProvider } from "./contexts/CurrentUserContext";
import { ColumnDefaultsProvider } from "./contexts/ColumnDefaultsContext";
import UploadDocument from "./pages/upload_document/upload_document";
import FileViewer from "./pages/document_library/file_viewer";
import Preview from "./pages/document_library/preview/preview";
import Home from "./pages/home";
import AccountMapping from "./pages/account_mapping";
import SupplierMapping from "./pages/supplier_mapping";
import ProcessMapping from "./pages/process_mapping";
import OpenPassword from "./pages/open_password";
import GenericCrud from "./pages/generic_crud";
import GenericCrudDetailWindow from "./pages/generic_crud_detail_window";
import TechnicalDetails from "./pages/technical_details";
import ReportsList from "./pages/reports/reports_list";
import ReportRunner from "./pages/reports/report_runner";
import ThemeEditor from "./pages/theme_editor";
import LoadingSplash from "./components/LoadingSplash";
import AppErrorBoundary from "./components/AppErrorBoundary";

LicenseManager.setLicenseKey("Using_this_{AG_Grid}_Enterprise_key_{AG-066909}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{eyebright_Ltd}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Customer_Portal}_only_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Customer_Portal}_need_to_be_licensed___{Customer_Portal}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{12_September_2025}____[v3]_[01]_MTc1NzYzMTYwMDAwMA==eb9b4c72adb3a66178af64f116534471");

function App({ msalInstance } : {msalInstance: PublicClientApplication }) {
  return (
    <MsalProvider instance={msalInstance }>
      {/* ChakraProvider wraps the auth gate, not the other way round - it's a styling context,
          not something that should depend on auth state, and LoadingSplash (rendered by
          MsalAuthenticationTemplate below while the redirect/token round-trip is pending) uses
          semantic tokens that only resolve with this theme in scope. */}
      <ChakraProvider theme={theme} portalZIndex={2}>
        {/* Inside ChakraProvider so the fallback UI has the theme, outside everything else so a
            throw in any provider or page is caught rather than blanking the whole app. */}
        <AppErrorBoundary>
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} loadingComponent={LoadingSplash}>
          <CurrentUserProvider>
            <ColumnDefaultsProvider>
              <NotificationPane>
                <ValidUserRoute>
                  <DynamicMenu>
                    <Route path="/home" component={Home} />
                    <Route path="/"><Redirect to="/home" /></Route>
                    <Route path="/document_library" component={DocumentLibrary}/>
                    <Route path="/doc_library"><Redirect to="/document_library" /></Route>
                    <Route path="/doc-library"><Redirect to="/document_library" /></Route>
                    <Route path="/upload_document" component={UploadDocument}/>
                    <Route path="/upload"><Redirect to="/upload_document" /></Route>
                    <Route path="/account_mapping" component={AccountMapping}/>
                    <Route path="/reference"><Redirect to="/account_mapping" /></Route>
                    <Route path="/supplier_mapping" component={SupplierMapping}/>
                    <Route path="/process_mapping" component={ProcessMapping}/>
                    <Route path="/process-mapping"><Redirect to="/process_mapping" /></Route>
                    <Route path="/open_password" component={OpenPassword}/>
                    <Route path="/open-password"><Redirect to="/open_password" /></Route>
                    <Route path="/technical_details" component={TechnicalDetails}/>
                    {/* /details is the original shipped path; /release_notes was an interim name
                        that only ever existed in unreleased work - kept purely so in-flight dev
                        links and open tabs don't 404. */}
                    <Route path="/details"><Redirect to="/technical_details" /></Route>
                    <Route path="/release_notes"><Redirect to="/technical_details" /></Route>
                    {/* Must come before /generic_crud/:navPath? - wouter matches in order, and the
                        optional :navPath? param would otherwise swallow this literal path too. */}
                    <Route path="/generic_crud/detail_window" component={GenericCrudDetailWindow} />
                    <Route path="/generic_crud/:navPath?">
                      {(params) => <GenericCrud navPath={params.navPath} />}
                    </Route>
                    <Route path="/generic-crud"><Redirect to="/generic_crud" /></Route>
                    <Route path="/file_viewer/:id">
                      {({ id }) => <FileViewer id={id} />}
                    </Route>
                    <Route path="/preview/:id">
                      {({ id }) => <Preview id={id} />}
                    </Route>
                    {/* Standalone - no dependency on the Generic CRUD engine above. */}
                    <Route path="/reports" component={ReportsList} />
                    {/* :slug not :sk - a readable name in the URL, resolved back to the report's
                        real sk inside ReportRunner (see its own comment). */}
                    <Route path="/reports/:slug">
                      {({ slug }) => <ReportRunner slug={slug} />}
                    </Route>
                    {/* Not linked from the NavBar - a colour-scheme editing tool, reachable by
                        direct URL only. See office-portal/DEVNOTES.md §1.12. */}
                    <Route path="/theme_editor" component={ThemeEditor} />
                    <Route path="/theme-editor"><Redirect to="/theme_editor" /></Route>
                  </DynamicMenu>
                </ValidUserRoute>
              </NotificationPane>
            </ColumnDefaultsProvider>
          </CurrentUserProvider>
        </MsalAuthenticationTemplate>
        </AppErrorBoundary>
      </ChakraProvider>
    </MsalProvider>
  )
}

export default App
